import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Youtube, Instagram } from "lucide-react";
import { SiTiktok } from "react-icons/si";

import { useTranslation } from 'react-i18next';
interface CalendarEvent {
    id: string;
    day: number;
    title: string;
    platform: "youtube" | "instagram" | "tiktok";
}

const platformStyles: Record<string, { bg: string; icon: React.ReactNode }> = {
    youtube: { bg: "bg-red-500", icon: <Youtube className="size-2.5 text-white" /> },
    instagram: { bg: "bg-gradient-to-r from-pink-500 to-purple-500", icon: <Instagram className="size-2.5 text-white" /> },
    tiktok: { bg: "bg-gray-800", icon: <SiTiktok className="size-2.5 text-white" /> },
};

const events: CalendarEvent[] = [
    { id: "1", day: 3, title: "Product Review", platform: "youtube" },
    { id: "2", day: 5, title: "Reel: Quick Tips", platform: "instagram" },
    { id: "3", day: 8, title: "Trend Video", platform: "tiktok" },
    { id: "4", day: 10, title: "Tutorial Ep. 5", platform: "youtube" },
    { id: "5", day: 12, title: "Collab Post", platform: "instagram" },
    { id: "6", day: 15, title: "Q&A Session", platform: "tiktok" },
    { id: "7", day: 17, title: "Behind Scenes", platform: "instagram" },
    { id: "8", day: 20, title: "How-To Guide", platform: "youtube" },
    { id: "9", day: 22, title: "Story Takeover", platform: "instagram" },
    { id: "10", day: 25, title: "Brand Reveal", platform: "tiktok" },
    { id: "11", day: 27, title: "Milestone!", platform: "youtube" },
];

export const AnimatedCalendarPreview = () => {
    const { t } = useTranslation();
    const [visibleEvents, setVisibleEvents] = useState(0);
    const [dragEvent, setDragEvent] = useState<{ id: string; fromDay: number; toDay: number } | null>(null);
    const [currentEvents, setCurrentEvents] = useState(events);

    useEffect(() => {
        const timers: ReturnType<typeof setTimeout>[] = [];

        // Fade events in one-by-one
        events.forEach((_, i) => {
            timers.push(
                setTimeout(() => setVisibleEvents(i + 1), 300 + i * 200)
            );
        });

        // After all visible, simulate a drag
        const totalAppear = 300 + events.length * 200;
        timers.push(
            setTimeout(() => {
                setDragEvent({ id: "6", fromDay: 15, toDay: 18 });
            }, totalAppear + 1000)
        );

        // Complete drag
        timers.push(
            setTimeout(() => {
                setCurrentEvents((prev) =>
                    prev.map((e) => (e.id === "6" ? { ...e, day: 18 } : e))
                );
                setDragEvent(null);
            }, totalAppear + 2200)
        );

        // Reset cycle
        const cycleTime = totalAppear + 5000;
        const interval = setInterval(() => {
            setVisibleEvents(0);
            setDragEvent(null);
            setCurrentEvents(events);

            events.forEach((_, i) => {
                timers.push(setTimeout(() => setVisibleEvents(i + 1), 300 + i * 200));
            });

            timers.push(
                setTimeout(() => setDragEvent({ id: "6", fromDay: 15, toDay: 18 }), totalAppear + 1000)
            );
            timers.push(
                setTimeout(() => {
                    setCurrentEvents((prev) => prev.map((e) => (e.id === "6" ? { ...e, day: 18 } : e)));
                    setDragEvent(null);
                }, totalAppear + 2200)
            );
        }, cycleTime);

        return () => {
            timers.forEach(clearTimeout);
            clearInterval(interval);
        };
    }, []);

    // Build a 5×7 calendar grid (Mon-Sun, 5 weeks for March)
    const daysInMonth = 31;
    const startDayOffset = 5; // March 2025 starts on Saturday (index 5 in Mon-start)
    const totalCells = 35;

    const cells: (number | null)[] = [];
    for (let i = 0; i < totalCells; i++) {
        const day = i - startDayOffset + 1;
        cells.push(day >= 1 && day <= daysInMonth ? day : null);
    }

    const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden h-full">
            {/* Top Bar */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                <span className="ml-2 text-xs font-semibold text-gray-500">{t('landing.mock.calendarView')}</span>
            </div>

            {/* Month Toolbar */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <ChevronLeft className="size-3.5 text-gray-500" />
                </button>
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">March 2025</span>
                <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <ChevronRight className="size-3.5 text-gray-500" />
                </button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-800">
                {weekdays.map((d) => (
                    <div key={d} className="py-1.5 text-center text-[8px] font-bold text-gray-400 uppercase tracking-wider">
                        {d}
                    </div>
                ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
                {cells.map((day, i) => {
                    const dayEvents = day ? currentEvents.filter((e) => e.day === day && (!dragEvent || e.id !== dragEvent.id || dragEvent.toDay === day)) : [];
                    const isDragTarget = dragEvent && day === dragEvent.toDay;

                    return (
                        <div
                            key={i}
                            className={`border-b border-r border-gray-100 dark:border-gray-800 p-1 min-h-[42px] transition-all duration-300 ${isDragTarget ? "bg-primary-50 dark:bg-primary-900/20" : ""
                                } ${!day ? "bg-gray-50/50 dark:bg-gray-800/30" : ""}`}
                        >
                            {day && (
                                <>
                                    <span className="text-[9px] font-medium text-gray-500 dark:text-gray-500">{day}</span>
                                    <div className="space-y-0.5 mt-0.5">
                                        {dayEvents.map((ev) => {
                                            const style = platformStyles[ev.platform];
                                            if (!style) return null;
                                            const evIdx = events.findIndex((e) => e.id === ev.id);
                                            const isVisible = evIdx < visibleEvents;

                                            return (
                                                <div
                                                    key={ev.id}
                                                    className={`flex items-center gap-0.5 ${style.bg} rounded px-1 py-0.5 transition-all duration-500 ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-75"
                                                        }`}
                                                >
                                                    {style.icon}
                                                    <span className="text-[7px] text-white font-medium truncate">{ev.title}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
